import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Package, Box, Loader2, AlertCircle, CheckCircle } from "lucide-react";
import { toast } from "react-toastify";
import {
  getOrderDetails,
  updatePackaging,
  getAdditionalServices,
  getPickPackFees,
  AdditionalService,
  PackagingData,
} from "core/Services/MerchantServices";
import { iOrderDetails } from "core/kangarooTypes/iOrderDetails";
import Button from "shared/Button";

const PackagingForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const merchantId = new URLSearchParams(location.search).get("merchantId");

  const [order, setOrder] = useState<iOrderDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [itemServices, setItemServices] = useState<AdditionalService[]>([]);
  const [orderServices, setOrderServices] = useState<AdditionalService[]>([]);
  const [pickPackFees, setPickPackFees] = useState<any[]>([]);

  // Selected services state with quantity
  const [selectedItemServices, setSelectedItemServices] = useState<{
    [itemId: number]: { [serviceId: number]: number };
  }>({});
  const [selectedOrderServices, setSelectedOrderServices] = useState<number[]>(
    []
  );

  useEffect(() => {
    if (id) {
      loadData();
    }
  }, [id]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [orderResponse, servicesResponse, feesResponse] = await Promise.all(
        [getOrderDetails(id!), getAdditionalServices(), getPickPackFees()]
      );

      setOrder(orderResponse.data);

      // Separate item-level and order-level services
      const services = servicesResponse.data || [];
      setItemServices(services.filter((s) => s.service_level === "item"));
      setOrderServices(services.filter((s) => s.service_level === "order"));

      setPickPackFees(feesResponse.data || []);

      // Initialize selected services from existing data with quantities
      const initialItemServices: {
        [itemId: number]: { [serviceId: number]: number };
      } = {};
      orderResponse.data.items?.forEach((item: any) => {
        if (item.item_services) {
          initialItemServices[item.id] = {};
          item.item_services.forEach((s: any) => {
            initialItemServices[item.id][s.service_id] = s.quantity || 1;
          });
        }
      });
      setSelectedItemServices(initialItemServices);

      if (orderResponse.data.order_services) {
        setSelectedOrderServices(
          orderResponse.data.order_services.map((s: any) => s.service_id)
        );
      }
    } catch (error) {
      toast.error("Failed to load order details");
    } finally {
      setIsLoading(false);
    }
  };

  const handleItemServiceToggle = (itemId: number, serviceId: number) => {
    setSelectedItemServices((prev) => {
      const current = prev[itemId] || {};
      const newServices = { ...current };

      if (serviceId in newServices) {
        // Remove service if already selected
        delete newServices[serviceId];
      } else {
        // Add service with quantity 1 by default
        newServices[serviceId] = 1;
      }

      return { ...prev, [itemId]: newServices };
    });
  };

  const handleItemServiceQuantityChange = (
    itemId: number,
    serviceId: number,
    quantity: number
  ) => {
    setSelectedItemServices((prev) => {
      const current = prev[itemId] || {};
      return {
        ...prev,
        [itemId]: {
          ...current,
          [serviceId]: Math.max(1, quantity), // Ensure minimum quantity is 1
        },
      };
    });
  };

  const handleOrderServiceToggle = (serviceId: number) => {
    setSelectedOrderServices((prev) => {
      if (prev.includes(serviceId)) {
        return prev.filter((id) => id !== serviceId);
      } else {
        return [...prev, serviceId];
      }
    });
  };

  const calculatePickPackFee = (): number => {
    if (!order?.items) return 0;

    const availableItemsCount = order.items
      .filter((item: any) => item.is_available !== false)
      .reduce((sum: number, item: any) => sum + item.quantity, 0);

    const fee = pickPackFees.find(
      (f) =>
        f.min_items <= availableItemsCount && f.max_items >= availableItemsCount
    );

    return fee?.price ? Number(fee.price) : 0;
  };

  const calculateTotalServicesFee = (): number => {
    let total = 0;

    // Add item services fees (price * quantity for each service)
    Object.entries(selectedItemServices).forEach(([itemId, services]) => {
      const item = order?.items?.find((i: any) => i.id === Number(itemId));
      if (item?.is_available !== false) {
        Object.entries(services).forEach(([serviceId, quantity]) => {
          const service = itemServices.find((s) => s.id === Number(serviceId));
          if (service) {
            total += Number(service.price) * quantity;
          }
        });
      }
    });

    // Add order services fees
    selectedOrderServices.forEach((serviceId) => {
      const service = orderServices.find((s) => s.id === serviceId);
      if (service) {
        total += Number(service.price);
      }
    });

    return total;
  };

  const handleSave = async () => {
    if (!order) return;

    setIsSaving(true);
    try {
      const packagingData: PackagingData = {
        item_services: Object.entries(selectedItemServices).map(
          ([itemId, services]) => ({
            item_id: Number(itemId),
            services: Object.entries(services).map(([serviceId, quantity]) => ({
              service_id: Number(serviceId),
              quantity: quantity,
            })),
          })
        ),
        order_services: selectedOrderServices,
      };

      await updatePackaging(order.id, packagingData);
      toast.success("Packaging completed successfully");
      const detailsUrl = `/order-details/:id=${
        order.id
      }?pageTitle=Order Details${
        merchantId ? `&merchantId=${merchantId}` : ""
      }`;
      navigate(detailsUrl);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "Failed to complete packaging"
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-500">Order not found</p>
      </div>
    );
  }

  const availableItems =
    order.items?.filter((item: any) => item.is_available !== false) || [];
  const unavailableItems =
    order.items?.filter((item: any) => item.is_available === false) || [];

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">
          Packaging - Order #{order.external_order_number || order.id}
        </h1>
        <span
          className={`px-3 py-1 rounded-full text-sm ${
            order.status === "ready_for_packaging"
              ? "bg-green-100 text-green-800"
              : "bg-yellow-100 text-yellow-800"
          }`}
        >
          {order.status?.replace(/_/g, " ")}
        </span>
      </div>

      {/* Unavailable Items Warning */}
      {unavailableItems.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2 text-yellow-800 font-medium mb-2">
            <AlertCircle className="w-5 h-5" />
            Some items are unavailable
          </div>
          <p className="text-sm text-yellow-700 mb-2">
            The following items cannot be packaged due to missing or
            insufficient inventory:
          </p>
          <ul className="text-sm text-yellow-700 list-disc list-inside">
            {unavailableItems.map((item: any) => (
              <li key={item.id}>
                {item.name || item.sku} (Qty: {item.quantity})
                {item.note && (
                  <span className="text-yellow-600"> - {item.note}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Available Items - Select Services */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Package className="w-5 h-5" />
          Item Services
        </h2>

        {availableItems.length === 0 ? (
          <p className="text-gray-500">No available items to package</p>
        ) : (
          <div className="space-y-4">
            {availableItems.map((item: any) => (
              <div key={item.id} className="border rounded-lg p-4">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <p className="font-medium">{item.name || item.sku}</p>
                    <p className="text-sm text-gray-500">
                      SKU: {item.sku} | Qty: {item.quantity}
                    </p>
                  </div>
                  <CheckCircle className="w-5 h-5 text-green-500" />
                </div>

                <div className="flex flex-wrap gap-3">
                  {itemServices.map((service) => {
                    const isSelected =
                      selectedItemServices[item.id]?.[service.id] !== undefined;
                    const quantity =
                      selectedItemServices[item.id]?.[service.id] || 1;

                    return (
                      <div
                        key={service.id}
                        className={`flex items-center gap-2 px-3 py-2 border rounded-lg transition-colors ${
                          isSelected
                            ? "border-blue-500 bg-blue-50"
                            : "border-gray-200 hover:border-blue-300"
                        }`}
                      >
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() =>
                              handleItemServiceToggle(item.id, service.id)
                            }
                            className="w-4 h-4 text-blue-600"
                          />
                          <span className="text-sm font-medium">
                            {service.name}
                          </span>
                          <span className="text-xs text-gray-500">
                            ({Number(service.price).toFixed(3)} JOD)
                          </span>
                        </label>

                        {isSelected && (
                          <div className="flex items-center gap-1 ml-2 border-l pl-2">
                            <label className="text-xs text-gray-600">
                              Qty:
                            </label>
                            <input
                              type="number"
                              min="1"
                              max={item.quantity}
                              value={quantity}
                              onChange={(e) =>
                                handleItemServiceQuantityChange(
                                  item.id,
                                  service.id,
                                  parseInt(e.target.value) || 1
                                )
                              }
                              className="w-16 px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              onClick={(e) => e.stopPropagation()}
                            />
                            <span className="text-xs text-gray-500">
                              / {item.quantity}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Order-Level Services */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Box className="w-5 h-5" />
          Order Services
        </h2>

        <div className="flex flex-wrap gap-3">
          {orderServices.map((service) => (
            <label
              key={service.id}
              className={`flex items-center gap-2 px-4 py-3 border rounded-lg cursor-pointer transition-colors ${
                selectedOrderServices.includes(service.id)
                  ? "border-blue-500 bg-blue-50"
                  : "border-gray-200 hover:border-blue-300"
              }`}
            >
              <input
                type="checkbox"
                checked={selectedOrderServices.includes(service.id)}
                onChange={() => handleOrderServiceToggle(service.id)}
                className="w-4 h-4 text-blue-600"
              />
              <div>
                <span className="text-sm font-medium">{service.name}</span>
                {service.description && (
                  <p className="text-xs text-gray-500">{service.description}</p>
                )}
              </div>
              <span className="text-sm text-gray-500 ml-auto">
                {Number(service.price).toFixed(3)} JOD
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Fee Summary */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">Fee Summary</h2>

        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="text-gray-600">Pick & Pack Fee</span>
            <span className="font-medium">
              {calculatePickPackFee().toFixed(3)} JOD
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Services Fee</span>
            <span className="font-medium">
              {calculateTotalServicesFee().toFixed(3)} JOD
            </span>
          </div>
          <hr />
          <div className="flex justify-between text-lg">
            <span className="font-semibold">Total Additional Fees</span>
            <span className="font-bold text-blue-600">
              {(calculatePickPackFee() + calculateTotalServicesFee()).toFixed(
                3
              )}{" "}
              JOD
            </span>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4 justify-end">
        <Button
          variant="secondary"
          onClick={() =>
            navigate(
              `/order-details/:id=${order.id}?pageTitle=Order Details${
                merchantId ? `&merchantId=${merchantId}` : ""
              }`
            )
          }
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={isSaving || availableItems.length === 0}
        >
          {isSaving ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Saving...
            </>
          ) : (
            "Complete Packaging"
          )}
        </Button>
      </div>
    </div>
  );
};

export default PackagingForm;
